/**
 * Builds textured, flat meshes for the 3D map.
 *
 * Google's Polygon3DElement takes a fill COLOUR and nothing else — there is no
 * image, pattern or material property on it — so a product texture cannot be
 * painted into a 3D polygon the way the flat map's canvas overlay paints one.
 * The only element that carries imagery is Model3DElement, which loads a glTF.
 *
 * So a textured shape is turned into a mesh: its rings triangulated, its
 * vertices placed in metres around an anchor, UVs derived from those metres so
 * the material keeps its real-world size, and the lot written out as a
 * self-contained .glb with the texture embedded.
 */

/** A ring of positions in local metres: x east, y north. */
export interface FlatRing {
  x: number;
  y: number;
  /** Height above the anchor, in metres. */
  z: number;
}

export interface MeshInput {
  /** Outer ring first, then holes. Each open (no repeated closing vertex). */
  rings: FlatRing[][];
  /** How many metres one tile of the texture covers. */
  tileMetres: number;
  /** Texture rotation within the shape, in degrees. */
  rotationDeg: number;
}

export interface Mesh {
  positions: Float32Array;
  uvs: Float32Array;
  indices: Uint32Array;
}

// ── Triangulation ────────────────────────────────────────────────────────────

/**
 * Ear clipping, with holes bridged into the outer ring first.
 *
 * O(n²), which is the right trade here: these are drawn outlines — a few
 * corners, occasionally a generated circle — not imported terrain, and a
 * simple implementation that is easy to reason about beats a fast one whose
 * failure modes are subtle.
 */
export function triangulate(rings: FlatRing[][]): {ring: FlatRing[], indices: number[]} {
  let outer = withoutRepeats(rings[0] ?? []);
  if (outer.length < 3) {
    return {ring: [], indices: []};
  }
  // Counter-clockwise from here on, so "convex" means one thing throughout and
  // the bridging can rely on which side of an edge is inside.
  if (signedArea(outer) < 0) {
    outer = outer.slice().reverse();
  }
  const holes = rings.slice(1).map(withoutRepeats).filter(hole => hole.length >= 3);
  // A hole touching the outline gets a bridge of no length, whose ends then
  // repeat their neighbours.
  const ring = holes.length ? withoutRepeats(bridgeHoles(outer, holes)) : outer;

  // Work on indices into `ring` so bridged duplicates stay distinct.
  const remaining = ring.map((_, index) => index);
  const indices: number[] = [];

  let guard = remaining.length * remaining.length + 16;
  while (remaining.length > 3 && guard-- > 0) {
    // Where the outline runs out and straight back -- the two copies of a
    // bridge end once clipping brings them side by side, or a spike with no
    // width -- it encloses nothing, so that vertex goes first. Left in, its
    // back-to-back edges look like a real corner, and an ear beside it could
    // cover ground the outline only passes along.
    const spike = remaining.findIndex((_, i) => doublesBack(...around(ring, remaining, i)));
    if (spike >= 0) {
      remaining.splice(spike, 1);
      continue;
    }
    let clipped = false;
    for (let i = 0; i < remaining.length; i++) {
      if (!isEar(ring, remaining, i)) {
        continue;
      }
      const count = remaining.length;
      indices.push(remaining[(i - 1 + count) % count], remaining[i], remaining[(i + 1) % count]);
      remaining.splice(i, 1);
      clipped = true;
      break;
    }
    if (clipped) {
      continue;
    }
    // No ear left. A point on a straight run encloses nothing either, so it
    // can go without losing any of the fill, and taking it out can free the
    // next ear.
    const flat = remaining.findIndex((_, i) => orient(...around(ring, remaining, i)) === 0);
    if (flat < 0) {
      // Self-intersecting or degenerate input. Take what is there rather than
      // spinning: a slightly wrong fill beats a hung frame.
      break;
    }
    remaining.splice(flat, 1);
  }
  // The ring runs counter-clockwise, so a last triangle that does not has
  // nothing inside it: what is left cancels itself out.
  if (remaining.length === 3
      && orient(ring[remaining[0]], ring[remaining[1]], ring[remaining[2]]) > 0) {
    indices.push(remaining[0], remaining[1], remaining[2]);
  }
  return {ring, indices};
}

/** The vertex at `remaining[i]` with its neighbours: previous, itself, next. */
function around(ring: FlatRing[], remaining: number[], i: number): [FlatRing, FlatRing, FlatRing] {
  const count = remaining.length;
  return [ring[remaining[(i - 1 + count) % count]], ring[remaining[i]], ring[remaining[(i + 1) % count]]];
}

/** Whether the outline turns straight back at `curr`, or does not move at all. */
function doublesBack(prev: FlatRing, curr: FlatRing, next: FlatRing): boolean {
  return orient(prev, curr, next) === 0
    && (curr.x - prev.x) * (next.x - curr.x) + (curr.y - prev.y) * (next.y - curr.y) <= 0;
}

/** Closer than this, in metres, two vertices are the same point. */
const SAME_POINT = 1e-6;

function same(p: FlatRing, q: FlatRing): boolean {
  return Math.abs(p.x - q.x) < SAME_POINT && Math.abs(p.y - q.y) < SAME_POINT;
}

/**
 * The ring without vertices that repeat the one before them, the last wrapping
 * round to the first. A double click or an imported outline leaves them, and a
 * corner with no length on one side can be neither clipped nor clipped past:
 * one of them used to stop the mesh after its first triangle.
 */
function withoutRepeats(ring: FlatRing[]): FlatRing[] {
  const kept: FlatRing[] = [];
  ring.forEach(point => {
    if (!kept.length || !same(kept[kept.length - 1], point)) {
      kept.push(point);
    }
  });
  while (kept.length > 1 && same(kept[kept.length - 1], kept[0])) {
    kept.pop();
  }
  return kept;
}

/** Twice the signed area of p, q, r: positive when they turn counter-clockwise. */
function orient(p: FlatRing, q: FlatRing, r: FlatRing): number {
  return (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);
}

function signedArea(ring: FlatRing[]): number {
  let sum = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    sum += (ring[j].x - ring[i].x) * (ring[j].y + ring[i].y);
  }
  return sum / 2;
}

/** Whether the corner at `remaining[at]` can be cut off as a triangle. */
function isEar(ring: FlatRing[], remaining: number[], at: number): boolean {
  const count = remaining.length;
  const prev = remaining[(at - 1 + count) % count];
  const curr = remaining[at];
  const next = remaining[(at + 1) % count];
  const a = ring[prev], b = ring[curr], c = ring[next];
  // The ring runs counter-clockwise, so reflex and flat corners are not ears.
  if (orient(a, b, c) <= 0) {
    return false;
  }
  for (let j = 0; j < count; j++) {
    const index = remaining[j];
    if (index === prev || index === curr || index === next) {
      continue;
    }
    const p = ring[index];
    if (!inTriangle(a, b, c, p)) {
      continue;
    }
    const before = ring[remaining[(j - 1 + count) % count]];
    const after = ring[remaining[(j + 1) % count]];
    // A vertex on one of the ear's own corners is the other copy of a bridge
    // end, or a place the outline touches itself. Being there proves nothing;
    // it is in the way only if the outline leaves it INTO the triangle.
    // Counting it as inside, like any other point on the edge, left the two
    // copies of each bridge end blocking each other, and a shape with a hole
    // got no mesh at all.
    const corner = same(p, a) ? [a, b, c] : same(p, b) ? [b, c, a] : same(p, c) ? [c, a, b] : null;
    if (corner) {
      if (entersCorner(corner, before) || entersCorner(corner, after)) {
        return false;
      }
      continue;
    }
    // Anywhere else only a reflex or flat vertex can be in the way: if the
    // outline reaches into the triangle at all, the vertex where it reaches
    // furthest is one. A convex vertex merely touching the cut, as the far
    // copy of a bridge end can once its neighbour is gone, is not.
    if (orient(before, p, after) <= 0) {
      return false;
    }
  }
  return true;
}

/**
 * Whether an edge from the first corner of a counter-clockwise triangle to q
 * runs into the triangle, rather than along or away from it.
 */
function entersCorner([corner, next, prev]: FlatRing[], q: FlatRing): boolean {
  return orient(corner, next, q) > 0 && orient(corner, prev, q) < 0;
}

function inTriangle(a: FlatRing, b: FlatRing, c: FlatRing, p: FlatRing): boolean {
  const d1 = (p.x - b.x) * (a.y - b.y) - (a.x - b.x) * (p.y - b.y);
  const d2 = (p.x - c.x) * (b.y - c.y) - (b.x - c.x) * (p.y - c.y);
  const d3 = (p.x - a.x) * (c.y - a.y) - (c.x - a.x) * (p.y - a.y);
  const hasNeg = d1 < 0 || d2 < 0 || d3 < 0;
  const hasPos = d1 > 0 || d2 > 0 || d3 > 0;
  return !(hasNeg && hasPos);
}

/**
 * Cuts each hole into the counter-clockwise outer ring with a bridge from the
 * hole's westmost vertex, duplicating both ends of the bridge so the ring
 * stays a single closed loop.
 */
function bridgeHoles(outer: FlatRing[], holes: FlatRing[][]): FlatRing[] {
  let ring = outer.slice();
  // Westmost first: a bridge runs west, so any hole it could pass on the way
  // is already part of the ring and gets bridged to rather than crossed.
  const ordered = holes.slice().sort((a, b) => {
    const left = a[leftmostIndex(a)], right = b[leftmostIndex(b)];
    return left.x - right.x || left.y - right.y;
  });

  for (const hole of ordered) {
    // Holes must wind against the outer ring or the bridged loop self-cancels.
    const wound = signedArea(hole) > 0 ? hole.slice().reverse() : hole.slice();
    const start = leftmostIndex(wound);
    const bridgeOuter = findBridge(ring, wound[start]);
    if (bridgeOuter < 0) {
      // Nothing west of it: the hole lies outside the shape.
      continue;
    }
    const rotated = wound.slice(start).concat(wound.slice(0, start));

    ring = [
      ...ring.slice(0, bridgeOuter + 1),
      ...rotated,
      rotated[0],
      ring[bridgeOuter],
      ...ring.slice(bridgeOuter + 1),
    ];
  }
  return ring;
}

/**
 * The ring vertex a hole vertex can see, to bridge to: David Eberly's method,
 * as earcut implements it.
 *
 * The nearest vertex will not do. Across a bay of a concave outline, or past
 * a hole bridged earlier, the nearest one can sit behind an edge, and a bridge
 * through that edge folds the ring over itself. Instead a ray goes west from
 * the hole to the nearest edge facing it; that edge's western end is the
 * answer unless a vertex pokes into the triangle between hole, hit and end,
 * in which case the one closest in angle to the ray is.
 */
function findBridge(ring: FlatRing[], hole: FlatRing): number {
  const n = ring.length;
  const touching = ring.findIndex(p => same(hole, p));
  if (touching >= 0) {
    return touching;
  }
  const hx = hole.x, hy = hole.y;
  let qx = -Infinity;
  let m = -1;
  for (let i = 0; i < n; i++) {
    const p = ring[i], next = ring[(i + 1) % n];
    // Edges running south are the ones with the inside to their east.
    if (hy <= p.y && hy >= next.y && next.y !== p.y) {
      const x = p.x + (hy - p.y) * (next.x - p.x) / (next.y - p.y);
      if (x <= hx && x > qx) {
        qx = x;
        m = p.x < next.x ? i : (i + 1) % n;
        if (x === hx) {
          // The hole touches this edge; its western end is in plain sight.
          return m;
        }
      }
    }
  }
  if (m < 0) {
    return -1;
  }

  const end = ring[m];
  const hit = {x: qx, y: hy, z: 0};
  let tanMin = Infinity;
  for (let step = 0, start = m; step < n; step++) {
    const i = (start + step) % n;
    const p = ring[i];
    if (hx >= p.x && p.x >= end.x && hx !== p.x && inTriangle(hole, end, hit, p)) {
      const tan = Math.abs(hy - p.y) / (hx - p.x);
      if (locallyInside(ring, i, hole) && (tan < tanMin || (tan === tanMin
          && (p.x > ring[m].x || (p.x === ring[m].x && sectorContainsSector(ring, m, i)))))) {
        m = i;
        tanMin = tan;
      }
    }
  }
  return m;
}

/** Whether a diagonal from ring vertex i to b starts out inside the ring. */
function locallyInside(ring: FlatRing[], i: number, b: FlatRing): boolean {
  const n = ring.length;
  const a = ring[i], prev = ring[(i - 1 + n) % n], next = ring[(i + 1) % n];
  return orient(prev, a, next) > 0
    ? orient(a, b, next) <= 0 && orient(a, prev, b) <= 0
    : orient(a, b, prev) > 0 || orient(a, next, b) > 0;
}

/**
 * For two copies of one point, as a bridge leaves behind: whether the wedge of
 * inside at copy m contains the wedge at copy p.
 */
function sectorContainsSector(ring: FlatRing[], m: number, p: number): boolean {
  const n = ring.length;
  return orient(ring[(m - 1 + n) % n], ring[m], ring[(p - 1 + n) % n]) > 0
    && orient(ring[(p + 1) % n], ring[m], ring[(m + 1) % n]) > 0;
}

function leftmostIndex(ring: FlatRing[]): number {
  let best = 0;
  for (let i = 1; i < ring.length; i++) {
    if (ring[i].x < ring[best].x || (ring[i].x === ring[best].x && ring[i].y < ring[best].y)) {
      best = i;
    }
  }
  return best;
}

// ── Mesh ─────────────────────────────────────────────────────────────────────

/**
 * Turns rings into a mesh with real-world UVs.
 *
 * The 3D map places a model in a local metre frame that is Z-UP: +X east,
 * +Y north, +Z up. It is emphatically NOT glTF's own Y-up convention, and
 * getting that wrong does not merely mirror the shape -- it stands it on its
 * edge. Putting height in Y made every polygon a vertical wall running
 * east-west, whose apparent size varied with how far north each corner was,
 * which reads on screen as a shape at the wrong angle rather than as a wall.
 *
 * Measured, not assumed. A 2 x 16 m bar written along Y lies north-south
 * across a ruler of polylines placed at known metre offsets; the same bar
 * written along Z renders as a tapering, receding plane -- unmistakable at
 * scale 4, which is how the wall was finally spotted.
 *
 * UVs come from the metre coordinates divided by the tile size, which is what
 * keeps a 1 m shingle 1 m wide however big the shape is, and matches how the
 * flat map's overlay scales the same texture.
 */
export function buildMesh(input: MeshInput): Mesh | null {
  const {ring, indices} = triangulate(input.rings);
  if (!ring.length || indices.length < 3) {
    return null;
  }
  const tile = input.tileMetres > 0 ? input.tileMetres : 1;
  const angle = ((input.rotationDeg || 0) * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  const positions = new Float32Array(ring.length * 3);
  const uvs = new Float32Array(ring.length * 2);
  ring.forEach((point, i) => {
    positions[i * 3] = point.x;
    positions[i * 3 + 1] = point.y;
    positions[i * 3 + 2] = point.z;
    // UV space runs SOUTHWARD, which is what makes 3D agree with the flat map.
    // There the material is a canvas pattern under DOMMatrix(cos, sin, -sin,
    // cos), so the texture u-axis lands along (cos, sin) in canvas space where
    // +y is south. Laying u along (cos, sin) in an east/NORTH frame instead
    // mirrors the material north-south, which turns it the wrong way for every
    // rotation and flips the image itself at 0.
    const south = -point.y;
    uvs[i * 2] = (point.x * cos + south * sin) / tile;
    uvs[i * 2 + 1] = (-point.x * sin + south * cos) / tile;
  });

  return {positions, uvs, indices: new Uint32Array(indices)};
}

// ── glTF ─────────────────────────────────────────────────────────────────────

const GLB_MAGIC = 0x46546c67;
const CHUNK_JSON = 0x4e4f534a;
const CHUNK_BIN = 0x004e4942;
const FLOAT = 5126;
const UNSIGNED_INT = 5125;
const ARRAY_BUFFER = 34962;
const ELEMENT_ARRAY_BUFFER = 34963;
const REPEAT = 10497;
const CLAMP_TO_EDGE = 33071;
const LINEAR = 9729;
const LINEAR_MIPMAP_LINEAR = 9987;

/**
 * Writes a self-contained .glb: one textured, double-sided mesh.
 *
 * Everything is embedded — the texture included — because the element takes a
 * single src and the alternative would be a second request per shape from a
 * bucket that may or may not allow it.
 */
export function buildGlb(mesh: Mesh,
                         image: {bytes: Uint8Array, mimeType: string} | null,
                         opacity: number,
                         clamp = false): Uint8Array {
  const views: GlbView[] = [];
  const push = (data: ArrayBufferView, target?: number) => views.push({data, target}) - 1;
  const positionView = push(mesh.positions, ARRAY_BUFFER);
  const uvView = push(mesh.uvs, ARRAY_BUFFER);
  const indexView = push(mesh.indices, ELEMENT_ARRAY_BUFFER);
  const imageView = image ? push(image.bytes) : -1;

  const alpha = Math.max(0, Math.min(1, opacity));
  // OPAQUE tells the renderer to ignore alpha altogether, the texture's own
  // included, so the see-through parts of a lattice or cut-out paver came out
  // as whatever colour the file keeps there -- black, as a rule -- where the
  // flat map shows the imagery through. BLEND below full opacity already
  // honours the texture's alpha; at full opacity MASK cuts those texels out
  // and, on a texture that is opaque after all, looks exactly like OPAQUE.
  const seeThrough = !!image && mayHaveAlpha(image.bytes);
  const alphaMode = alpha < 1 ? 'BLEND' : seeThrough ? 'MASK' : 'OPAQUE';
  const gltf: any = {
    asset: {version: '2.0', generator: 'measure-map-online'},
    scene: 0,
    scenes: [{nodes: [0]}],
    nodes: [{mesh: 0}],
    meshes: [{
      primitives: [{
        attributes: {POSITION: 0, TEXCOORD_0: 1},
        indices: 2,
        material: 0,
      }],
    }],
    materials: [{
      pbrMetallicRoughness: {
        baseColorFactor: [1, 1, 1, alpha],
        metallicFactor: 0,
        roughnessFactor: 1,
        ...(image ? {baseColorTexture: {index: 0}} : {}),
      },
      // Seen from below when the camera drops under a raised roof plane, and
      // the material is flat anyway, so lighting it from one side only would
      // just make it vanish at some angles.
      doubleSided: true,
      alphaMode,
      ...(alphaMode === 'MASK' ? {alphaCutoff: 0.5} : {}),
    }],
    accessors: [
      positionAccessor(positionView, mesh.positions),
      {
        bufferView: uvView, componentType: FLOAT, count: mesh.uvs.length / 2, type: 'VEC2',
      },
      {
        bufferView: indexView, componentType: UNSIGNED_INT, count: mesh.indices.length,
        type: 'SCALAR',
      },
    ],
  };

  if (image) {
    gltf.images = [{bufferView: imageView, mimeType: image.mimeType}];
    // REPEAT is the whole point: the mesh's UVs run past 1 so the material
    // tiles across the shape at its real size.
    // A picture laid over the map once (`clamp`) is not tiled: its edges are
    // not blended with the far side.
    gltf.samplers = [{
      wrapS: clamp ? CLAMP_TO_EDGE : REPEAT, wrapT: clamp ? CLAMP_TO_EDGE : REPEAT,
      magFilter: LINEAR, minFilter: LINEAR_MIPMAP_LINEAR,
    }];
    gltf.textures = [{sampler: 0, source: 0}];
  }
  return packGlb(gltf, views);
}

/** A run of binary data for a .glb, and what it is bound to (ARRAY_BUFFER, ELEMENT_ARRAY_BUFFER, or nothing). */
export interface GlbView {
  data: ArrayBufferView;
  target?: number;
}

export const GLB_ARRAY_BUFFER = ARRAY_BUFFER;
export const GLB_ELEMENT_ARRAY_BUFFER = ELEMENT_ARRAY_BUFFER;
export const GLB_FLOAT = FLOAT;
export const GLB_UNSIGNED_INT = UNSIGNED_INT;

/**
 * The POSITION accessor of a mesh, with the bounds it must carry. These are
 * not decoration: the renderer culls against them, and a mesh whose vertices
 * reach past its declared box simply never appears.
 */
export function positionAccessor(bufferView: number, positions: Float32Array): any {
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  for (let i = 0; i < positions.length; i += 3) {
    minX = Math.min(minX, positions[i]);
    maxX = Math.max(maxX, positions[i]);
    minY = Math.min(minY, positions[i + 1]);
    maxY = Math.max(maxY, positions[i + 1]);
    minZ = Math.min(minZ, positions[i + 2]);
    maxZ = Math.max(maxZ, positions[i + 2]);
  }
  return {
    bufferView, componentType: FLOAT, count: positions.length / 3,
    type: 'VEC3', min: [minX, minY, minZ], max: [maxX, maxY, maxZ],
  };
}

/**
 * Packs a glTF document and its binary data into one .glb. `views` become
 * the document's bufferViews, in order, so its accessors and images refer to
 * them by index; each starts 4-byte aligned, which the spec requires for
 * accessors.
 */
export function packGlb(gltf: any, views: GlbView[]): Uint8Array {
  const bufferViews: any[] = [];
  const chunks: Uint8Array[] = [];
  let offset = 0;
  views.forEach(view => {
    const data = new Uint8Array(view.data.buffer, view.data.byteOffset, view.data.byteLength);
    const padding = (4 - (offset % 4)) % 4;
    if (padding) {
      chunks.push(new Uint8Array(padding));
      offset += padding;
    }
    bufferViews.push({
      buffer: 0,
      byteOffset: offset,
      byteLength: data.byteLength,
      ...(view.target ? {target: view.target} : {}),
    });
    chunks.push(data);
    offset += data.byteLength;
  });

  const json = new TextEncoder().encode(JSON.stringify({...gltf, bufferViews, buffers: [{byteLength: offset}]}));
  const jsonPadding = (4 - (json.byteLength % 4)) % 4;
  const binPadding = (4 - (offset % 4)) % 4;
  const jsonLength = json.byteLength + jsonPadding;
  const binLength = offset + binPadding;
  const total = 12 + 8 + jsonLength + 8 + binLength;

  const glb = new Uint8Array(total);
  const view = new DataView(glb.buffer);
  view.setUint32(0, GLB_MAGIC, true);
  view.setUint32(4, 2, true);
  view.setUint32(8, total, true);
  view.setUint32(12, jsonLength, true);
  view.setUint32(16, CHUNK_JSON, true);
  glb.set(json, 20);
  // JSON pads with spaces, BIN with zeroes — the spec is specific about this.
  glb.fill(0x20, 20 + json.byteLength, 20 + jsonLength);

  const binHeader = 20 + jsonLength;
  view.setUint32(binHeader, binLength, true);
  view.setUint32(binHeader + 4, CHUNK_BIN, true);
  let cursor = binHeader + 8;
  chunks.forEach(chunk => {
    glb.set(chunk, cursor);
    cursor += chunk.byteLength;
  });

  return glb;
}

/**
 * Whether an encoded image can hold see-through pixels, read from its header.
 *
 * The glTF is written synchronously from bytes that are already encoded, so
 * the pixels themselves are out of reach here; the header says whether there
 * is an alpha channel or a transparent colour at all. That over-reports --
 * plenty of PNGs carry an alpha channel they never use -- which costs nothing
 * under MASK. A texture shrunk on a canvas is kept as PNG only when it has
 * see-through pixels (Map3dComponent.shrinkTexture), so for those the answer
 * is exact. A format not recognised here counts as possibly see-through.
 */
function mayHaveAlpha(bytes: Uint8Array): boolean {
  const reads = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i++) {
      if (bytes[offset + i] !== text.charCodeAt(i)) {
        return false;
      }
    }
    return true;
  };
  // JPEG has no alpha at all.
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return false;
  }
  if (bytes[0] === 0x89 && reads(1, 'PNG')) {
    // IHDR always comes first; colour types 4 and 6 carry an alpha channel.
    const colourType = bytes[25];
    if (colourType === 4 || colourType === 6) {
      return true;
    }
    // Any other type is see-through only through a tRNS chunk, which the
    // format puts before the image data.
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    for (let offset = 8; offset + 8 <= bytes.length; offset += 12 + view.getUint32(offset)) {
      if (reads(offset + 4, 'tRNS')) {
        return true;
      }
      if (reads(offset + 4, 'IDAT') || reads(offset + 4, 'IEND')) {
        return false;
      }
    }
    return false;
  }
  if (reads(0, 'RIFF') && reads(8, 'WEBP')) {
    if (reads(12, 'VP8 ')) {
      // Lossy WebP on its own has no alpha.
      return false;
    }
    if (reads(12, 'VP8L')) {
      // Lossless: the alpha_is_used bit of the header after the signature.
      return (bytes[24] & 0x10) !== 0;
    }
    if (reads(12, 'VP8X')) {
      // Extended: the alpha flag.
      return (bytes[20] & 0x10) !== 0;
    }
  }
  return true;
}

/**
 * Base64, in chunks.
 *
 * String.fromCharCode(...bytes) in one go blows the argument limit on any
 * texture worth having, so this walks the array in slices small enough to
 * spread safely.
 */
export function base64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)));
  }
  return btoa(binary);
}

/**
 * Least-squares plane through measured heights, as h = a·x + b·y + c.
 *
 * Used to fill in corners the probe could not read. Close in on a shape and
 * some of its corners leave the screen, where they cannot be measured at all --
 * and holding out for every one of them dropped the material back to a flat
 * sheet, which is the very thing the probing is for. A roof plane is planar, so
 * three corners place the rest of them.
 *
 * Returns null when there are fewer than three, or when they are collinear and
 * so describe no plane at all.
 */
export function fitPlane(samples: Array<{x: number, y: number, h: number}>)
    : ((x: number, y: number) => number) | null {
  if (samples.length < 3) {
    return null;
  }
  // Normal equations for [a, b, c] against sums of x, y and 1.
  let sxx = 0, sxy = 0, sx = 0, syy = 0, sy = 0, n = 0;
  let sxh = 0, syh = 0, sh = 0;
  samples.forEach(({x, y, h}) => {
    sxx += x * x; sxy += x * y; sx += x;
    syy += y * y; sy += y; n += 1;
    sxh += x * h; syh += y * h; sh += h;
  });
  const m = [
    [sxx, sxy, sx],
    [sxy, syy, sy],
    [sx, sy, n],
  ];
  const rhs = [sxh, syh, sh];
  const det =
      m[0][0] * (m[1][1] * m[2][2] - m[1][2] * m[2][1])
    - m[0][1] * (m[1][0] * m[2][2] - m[1][2] * m[2][0])
    + m[0][2] * (m[1][0] * m[2][1] - m[1][1] * m[2][0]);
  // Collinear samples leave the system singular; there is no plane to find.
  if (!isFinite(det) || Math.abs(det) < 1e-9) {
    return null;
  }
  const solve = (column: number) => {
    const c = m.map((row, i) => row.map((value, j) => (j === column ? rhs[i] : value)));
    return (
        c[0][0] * (c[1][1] * c[2][2] - c[1][2] * c[2][1])
      - c[0][1] * (c[1][0] * c[2][2] - c[1][2] * c[2][0])
      + c[0][2] * (c[1][0] * c[2][1] - c[1][1] * c[2][0])
    ) / det;
  };
  const a = solve(0);
  const b = solve(1);
  const c = solve(2);
  return (x: number, y: number) => a * x + b * y + c;
}

// ── A picture laid over the map ─────────────────────────────────────────────

/** What a picture's sheet is made of (buildImageSheet). */
export interface ImageSheetInput {
  /** The picture's box, east-west and north-south, in metres. */
  widthMetres: number;
  heightMetres: number;
  /** Degrees it is turned clockwise about its middle, as on the flat map. */
  rotationDeg: number;
  /** Cells across and down. */
  columns: number;
  rows: number;
  /**
   * Heights over the middle's, in metres, at each grid point (sheetPoints'
   * order); none for a flat sheet.
   */
  heights?: number[] | null;
}

/**
 * The grid points of a picture's sheet, in local metres about its middle
 * (x east, y north), row by row from its north-west corner: turned as the
 * picture is, so the ground can be read under each one.
 */
export function sheetPoints(input: ImageSheetInput): Array<{x: number, y: number}> {
  const angle = ((input.rotationDeg || 0) * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const points: Array<{x: number, y: number}> = [];
  for (let row = 0; row <= input.rows; row++) {
    for (let column = 0; column <= input.columns; column++) {
      const x = -input.widthMetres / 2 + (input.widthMetres * column) / input.columns;
      const y = input.heightMetres / 2 - (input.heightMetres * row) / input.rows;
      // Clockwise seen from above, as CSS's rotate() turns it on the flat map.
      points.push({x: x * cos + y * sin, y: -x * sin + y * cos});
    }
  }
  return points;
}

/**
 * A picture's sheet: a grid over its box, turned as it is, each point at
 * its height over the middle when it follows the ground and at the middle's
 * own when it lies flat. The whole picture is stretched across it once, its
 * top-left on the north-west corner, as the flat map draws it.
 */
export function buildImageSheet(input: ImageSheetInput): Mesh | null {
  const columns = Math.max(1, Math.floor(input.columns));
  const rows = Math.max(1, Math.floor(input.rows));
  if (!(input.widthMetres > 0) || !(input.heightMetres > 0)) {
    return null;
  }
  const points = sheetPoints({...input, columns, rows});
  const heights = input.heights && input.heights.length === points.length ? input.heights : null;
  const positions = new Float32Array(points.length * 3);
  const uvs = new Float32Array(points.length * 2);
  points.forEach((point, i) => {
    positions[i * 3] = point.x;
    positions[i * 3 + 1] = point.y;
    positions[i * 3 + 2] = heights && Number.isFinite(heights[i]) ? heights[i] : 0;
    uvs[i * 2] = (i % (columns + 1)) / columns;
    uvs[i * 2 + 1] = Math.floor(i / (columns + 1)) / rows;
  });
  const indices: number[] = [];
  for (let row = 0; row < rows; row++) {
    for (let column = 0; column < columns; column++) {
      const a = row * (columns + 1) + column;
      const b = a + 1;
      const c = a + columns + 1;
      const d = c + 1;
      indices.push(a, c, b, b, c, d);
    }
  }
  return {positions, uvs, indices: new Uint32Array(indices)};
}
